import Controls from '@/components/game/Controls';
import GameBoard from '@/components/game/GameBoard';
import ProceduralBackground from '@/components/game/ProceduralBackground';
import MinimalHUD from '@/components/game/MinimalHUD';
import { PlayerStatusBar } from '@/components/ui';
import PowerupDisplay from '@/components/game/PowerupDisplay';
import Timer from '@/components/game/Timer';
import TouchControls from '@/components/game/TouchControls';
import Commentary from '@/components/game/Commentary';
import CardHand from '@/components/game/CardHand';
import CardPicker from '@/components/game/CardPicker';
import CardPlayEffect from '@/components/game/CardPlayEffect';
import SettingsMenuWrapper from '@/components/layout/SettingsMenuWrapper';
import type { LevelCompleteStats } from '@/components/modals';
import {
  DailyRewardModal,
  GameOverModal,
  LevelCompleteModal,
  LevelSelectModal,
  markObstacleTutorialShown,
  NoLivesModal,
  ObstacleTutorialModal,
  ShopModal,
} from '@/components/modals';
import { useShop } from '@/contexts/ShopContext';
import { useSound } from '@/contexts/SoundContext';
import { puzzles } from '@/game/puzzles';
import { getUpgradeManager } from '@/game/upgrades';
import { useGameEngine, getSavedInfiniteRunState, clearInfiniteRunState } from '@/hooks/useGameEngine';
import { useCardSystem } from '@/hooks/useCardSystem';
import { useKeyboardControls } from '@/hooks/useKeyboardControls';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useModal } from '@/hooks/useModal';
import { useFocusManager } from '@/hooks/useFocusManager';
import { StorageService } from '@/services/storage';
import { getCommentarySystem } from '@/game/commentarySystem';
import { CARD_DEFINITIONS } from '@/config/cards';
import type { CardType, CardRarity } from '@/types/card';
import type { Direction } from '@/types/game';
import { useEffect, useRef, useState, useCallback } from 'react';
import { getWorldForLevel } from '@/config/worlds';

interface GameScreenProps {
  onOpenSettings: () => void;
  onGoHome: () => void;
  onOpenShop?: () => void;
  onToggleStats?: () => void;
  onLevelSelect?: () => void;
}

function GameScreen({
  onOpenSettings: _onOpenSettings, // Now using modal instead
  onGoHome,
  onOpenShop = () => {},
  onToggleStats = () => {},
  onLevelSelect = () => {},
}: GameScreenProps) {
  const { gameState, movePlayer, restartLevel, loadLevel, loadInfiniteWave, loadNextLevel, hintPath, hintPathHazards, triggerLevelCompleteModal, restartCounter, setPaused, playCardEffect, warpToPosition } = useGameEngine();
  const { activeModal, openModal, closeModal, isModalOpen } = useModal();
  const isMobile = useIsMobile();
  const { shopState, shopManager } = useShop();
  const { playGameOver, playCardDraw, playCardPlay } = useSound();
  const cardSystem = useCardSystem();

  // Expose a small, stable "text mode" snapshot for automated testing.
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;
  const shopStateRef = useRef(shopState);
  shopStateRef.current = shopState;
  const shopManagerRef = useRef(shopManager);
  shopManagerRef.current = shopManager;
  const uiStateRef = useRef({
    activeModal: null as any,
    levelCompleteModalOpen: false,
    obstacleModalOpen: false,
    gameOverModalOpen: false,
    showNoLivesModal: false,
    showSettingsModal: false,
    isMovementLocked: false,
  });
  useEffect(() => {
    (window as any).render_game_to_text = () => {
      const s = gameStateRef.current;
      const remainingCollectibles = (s.collectibles || []).filter(c => !c.isCollected);

      return JSON.stringify({
        mode: 'game',
        route: `${window.location.pathname}${window.location.search}`,
        coords: {
          format: '[col,row]',
          origin: 'top-left',
          x: 'right',
          y: 'down',
        },
        level: s.level,
        isTutorial: Boolean(s.isTutorial),
        isPlaying: s.isPlaying,
        isPaused: s.isPaused,
        player: { col: s.position[0], row: s.position[1] },
        end: { col: s.end[0], row: s.end[1] },
        stats: {
          stepsTaken: s.gameStats.stepsTaken,
          score: s.gameStats.score,
          streak: s.gameStats.streak,
        },
        wallet: {
          coins: shopStateRef.current.coins,
          gems: shopStateRef.current.gems,
          lives: shopStateRef.current.lives,
          maxLives: shopManagerRef.current?.getMaxLives?.() ?? 5,
        },
        collectibles: {
          total: (s.collectibles || []).length,
          remaining: remainingCollectibles.length,
          collectedInLevel: s.collectedInLevel,
        },
        collision: {
          hasCollided: s.hasCollided,
        },
        metaUpgrades: getUpgradeManager().getState().levels,
        ui: uiStateRef.current,
      });
    };

    return () => {
      try {
        delete (window as any).render_game_to_text;
      } catch {
        // ignore
      }
    };
    // We intentionally keep a stable function and read current state via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Enhanced focus management for game mode
  const { isPaused, pauseReason, pause, resume } = useFocusManager({
    gameMode: true,
    pauseOnFocusLoss: true,
    onPause: () => {
      console.log('[GAME] Game paused due to focus loss');
      // Additional game-specific pause logic can go here
    },
    onResume: () => {
      console.log('[GAME] Game resumed after focus gain');
      // Additional game-specific resume logic can go here
    },
  });

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Level complete modal state
  const [levelCompleteModalOpen, setLevelCompleteModalOpen] = useState(false);
  const [levelWon, setLevelWon] = useState(false);
  const [levelCompleteStats, setLevelCompleteStats] = useState<LevelCompleteStats | null>(null);

  // Obstacle tutorial state
  const [obstacleModalOpen, setObstacleModalOpen] = useState(false);
  const [currentObstacle, setCurrentObstacle] = useState<string | null>(null);
  const currentObstacleRef = useRef<string | null>(null);
  currentObstacleRef.current = currentObstacle;

  // Game over modal state
  const [gameOverModalOpen, setGameOverModalOpen] = useState(false);
  const [gameOverStats, setGameOverStats] = useState<{
    level: number;
    score: number;
    steps: number;
  } | null>(null);

  // Tutorial instructions state
  const [instructionsText, setInstructionsText] = useState('');

  // Game pause/focus management
  const handleGamePause = useCallback(() => {
    if (gameState.isPlaying && !gameState.isPaused) {
      // Pause the game and show settings modal
      console.log('[GAME] Auto-pausing due to focus loss');
      openModal('settings');
    }
  }, [gameState.isPlaying, gameState.isPaused, openModal]);

  const handleGameResume = useCallback(() => {
    // Game will auto-resume when modal is closed
    console.log('[GAME] Focus regained - game will resume when modal closes');
  }, []);

  // Set up focus management for auto-pause
  useFocusManager({
    onFocusLost: handleGamePause,
    onFocusGained: handleGameResume,
    pauseOnFocusLoss: true,
    debounceMs: 150, // Slight delay to avoid rapid focus changes
  });

  // No lives modal state
  const [showNoLivesModal, setShowNoLivesModal] = useState(false);
  
  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // HUD expanded state (pause menu)
  const [hudExpanded, setHudExpanded] = useState(false);

  const handleHudToggle = useCallback(() => {
    setHudExpanded(prev => {
      const next = !prev;
      setPaused(next);
      return next;
    });
  }, [setPaused]);

  // Daily reward modal state
  const [showDailyReward, setShowDailyReward] = useState(false);

  // Auto-show daily reward modal on mount if reward is available
  useEffect(() => {
    if (!hasInitialized) return;
    const status = shopManager.getDailyRewardStatus();
    if (status.canClaim) {
      // Short delay for entrance animation
      const timer = setTimeout(() => setShowDailyReward(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [hasInitialized, shopManager]);

  // Card picker state
  const [showCardPicker, setShowCardPicker] = useState(false);

  // Card play effect state
  const [playedCard, setPlayedCard] = useState<{ type: CardType; rarity: CardRarity } | null>(null);
  const playedCardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show card picker when a level loads and the player has cards in inventory
  useEffect(() => {
    if (gameState.isPlaying && cardSystem.inventory.length > 0 && cardSystem.hand.length === 0) {
      setShowCardPicker(true);
      setPaused(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.level, gameState.isPlaying]);

  const handleCardPickerConfirm = useCallback((selectedIds: string[]) => {
    cardSystem.selectHand(selectedIds);
    setShowCardPicker(false);
    setPaused(false);
  }, [cardSystem, setPaused]);

  const handleCardPickerSkip = useCallback(() => {
    setShowCardPicker(false);
    setPaused(false);
  }, [setPaused]);

  // Handle playing a card from hand
  const handlePlayCard = useCallback((cardId: string) => {
    // Retrieve card info before playing (playCard removes it from hand)
    const cardInfo = cardSystem.hand.find(c => c.id === cardId);
    const cardType = cardSystem.playCard(cardId);
    if (cardType) {
      playCardPlay();
      playCardEffect(cardType);

      // Show card play overlay animation
      const rarity = cardInfo?.rarity ?? CARD_DEFINITIONS[cardType].rarity;
      setPlayedCard({ type: cardType, rarity });
      // Clear any previous timer
      if (playedCardTimerRef.current) clearTimeout(playedCardTimerRef.current);
      playedCardTimerRef.current = setTimeout(() => setPlayedCard(null), 1200);

      // Commentary narration for each card type
      const commentary = getCommentarySystem();
      const messages: Record<CardType, string> = {
        freeze: 'Freeze Card played! Enemies frozen for 2 turns',
        scout: 'Scout Card played! Path to exit revealed',
        shield: 'Shield Card played! Protected from next hit',
        warp: 'Warp Card played! Select a tile to teleport',
        rewind: 'Rewind Card played! Rewinding 3 moves',
      };
      commentary.trigger(messages[cardType], 'excited', 'high');
    }
  }, [cardSystem, playCardEffect, playCardPlay]);

  // Movement lock state for entrance/exit animations
  const [isMovementLocked, setIsMovementLocked] = useState(false);

  // Keep UI state available to window.render_game_to_text via ref.
  uiStateRef.current = {
    activeModal: activeModal ?? null,
    levelCompleteModalOpen,
    obstacleModalOpen,
    gameOverModalOpen,
    showNoLivesModal,
    showSettingsModal,
    isMovementLocked,
  };

  // Listen for game over event
  useEffect(() => {
    const handleGameOver = (event: any) => {
      const { level, score, steps } = event.detail;
      setGameOverStats({ level, score, steps });
      setGameOverModalOpen(true);
      playGameOver();
    };

    window.addEventListener('gameOver', handleGameOver);
    return () => window.removeEventListener('gameOver', handleGameOver);
  }, [playGameOver]);
  
  // Monitor lives and show modal if they drop to 0
  useEffect(() => {
    if (shopState.lives <= 0 && hasInitialized && !showNoLivesModal) {
      setShowNoLivesModal(true);
    }
  }, [shopState.lives, hasInitialized, showNoLivesModal]);

  // Listen for new obstacle event
  useEffect(() => {
    const handleNewObstacle = (event: any) => {
      const { obstacle } = event.detail;
      // Don't reopen if tutorial was already dismissed (e.g. via loadLevel setTimeout)
      const alreadyShown = localStorage.getItem(`obstacle_tutorial_${obstacle}`);
      if (alreadyShown) return;
      setCurrentObstacle(obstacle);
      setObstacleModalOpen(true);
    };

    window.addEventListener('newObstacle', handleNewObstacle);
    return () => window.removeEventListener('newObstacle', handleNewObstacle);
  }, []);
  
  // Listen for level complete event
  useEffect(() => {
    const handleLevelComplete = (event: any) => {
      const { score, stars, completionTime, steps, retries, keyPressSpeed, collectiblesCollected, collectiblesTotal } = event.detail;
      setLevelCompleteStats({
        stepsTaken: steps,
        timeSpent: completionTime,
        retries: retries ?? 0,
        keyPressSpeed: keyPressSpeed ?? 0,
        stars,
        score,
        collectiblesCollected: collectiblesCollected ?? 0,
        collectiblesTotal: collectiblesTotal ?? 0,
      });
      setLevelWon(true);
      setLevelCompleteModalOpen(true);

      // Roll for a card drop on level completion
      const droppedCard = cardSystem.rollDrop();
      if (droppedCard) {
        cardSystem.addToInventory(droppedCard);
        playCardDraw();

        // Show card tutorial the first time this card type is received
        const cardTutorialKey = `obstacle_tutorial_card_${droppedCard.type}`;
        if (!localStorage.getItem(cardTutorialKey)) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('newObstacle', {
              detail: { obstacle: `card_${droppedCard.type}` }
            }));
          }, 2000);
        }
      }
    };

    window.addEventListener('levelComplete', handleLevelComplete);
    return () => window.removeEventListener('levelComplete', handleLevelComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardSystem, playCardDraw]);
  
  // Listen for all levels complete event
  useEffect(() => {
    const handleAllLevelsComplete = () => {
      onGoHome();
    };

    window.addEventListener('allLevelsComplete', handleAllLevelsComplete);
    return () => window.removeEventListener('allLevelsComplete', handleAllLevelsComplete);
  }, [onGoHome]);

  // Set tutorial instructions based on device
  useEffect(() => {
    setInstructionsText(
      isMobile
        ? 'Swipe in any direction to move the player'
        : 'Use the arrow keys to move the player'
    );
  }, [isMobile]);

  // Initialize with appropriate level (from URL param or default to tutorial)
  useEffect(() => {
    // Only run initialization once
    if (hasInitialized) return;

    const urlParams = new URLSearchParams(window.location.search);
    const levelParam = urlParams.get('level');
    const modeParam = urlParams.get('mode');

    // Small delay to ensure smooth transition
    const loadTimeout = setTimeout(() => {
      // Check for infinite mode
      if (modeParam === 'infinite') {
        const freshParam = urlParams.get('fresh');
        if (freshParam === '1') {
          // Explicit new run – clear saved state
          clearInfiniteRunState();
          loadInfiniteWave(1);
        } else {
          // Resume from saved run, or start fresh if none
          const saved = getSavedInfiniteRunState();
          if (saved) {
            loadInfiniteWave(saved.wave, saved);
          } else {
            loadInfiniteWave(1);
          }
        }
      } else {
        // Determine which level to load
        let targetLevel = 0; // Default to tutorial

        if (levelParam) {
          const level = parseInt(levelParam, 10);
          if (!isNaN(level) && level >= 0 && level < puzzles.length) {
            targetLevel = level;
          }
        }

        // Load the level first (so correct level shows behind modal)
        loadLevel(targetLevel);

        // Save this as the last played level
        StorageService.saveLastPlayedLevel(targetLevel);
      }

      // Then check if player has lives
      if (shopState.lives <= 0) {
        setShowNoLivesModal(true);
      }

      setIsLoading(false);
      setHasInitialized(true);
    }, 100);

    return () => clearTimeout(loadTimeout);
  }, [loadLevel, loadInfiniteWave, hasInitialized, shopState.lives]);

  // Apply tutorial CSS class
  useEffect(() => {
    const gameElement = document.querySelector('.game');
    if (gameElement) {
      if (gameState.isTutorial) {
        gameElement.classList.add('game--tutorial');
      } else {
        gameElement.classList.remove('game--tutorial');
      }
    }
  }, [gameState.isTutorial]);

  // Watch for level completion and trigger modal - using event-based system only  
  const [_hasShownCompleteModal, setHasShownCompleteModal] = useState(false);

  // Disabled old completion detection - using event-based system only
  // useEffect(() => {
  //   // Only trigger if we haven't shown the modal for this level yet
  //   // and the game is not playing (indicating completion)
  //   // and position equals end position
  //   if (
  //     !gameState.isPlaying &&
  //     !hasShownCompleteModal &&
  //     gameState.position[0] === gameState.end[0] &&
  //     gameState.position[1] === gameState.end[1] &&
  //     // Make sure we have actual stats (not initial state)
  //     gameState.gameStats.stepsTaken > 0
  //   ) {
  //     // Level completed successfully
  //     const stats: LevelCompleteStats = {
  //       stepsTaken: gameState.gameStats.stepsTaken,
  //       timeSpent: gameState.gameStats.timeSpent,
  //       retries: gameState.gameStats.retries,
  //       keyPressSpeed: gameState.gameStats.keyPressSpeed,
  //       stars: calculateStars(gameState.gameStats),
  //       score: gameState.gameStats.score,
  //     };

  //     setLevelCompleteStats(stats);
  //     setLevelWon(true);
  //     setLevelCompleteModalOpen(true);
  //     setHasShownCompleteModal(true);
  //   }
  // }, [
  //   gameState.isPlaying,
  //   gameState.position,
  //   gameState.end,
  //   gameState.gameStats,
  //   hasShownCompleteModal,
  // ]);

  // Reset modal shown flag when level changes or restarts
  useEffect(() => {
    setHasShownCompleteModal(false);
  }, [gameState.level, restartCounter]);

  // Handle level changes from URL params (for smooth transitions)
  useEffect(() => {
    // Skip if not initialized yet
    if (!hasInitialized) return;

    const handleLocationChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const levelParam = urlParams.get('level');

      if (levelParam) {
        const newLevel = parseInt(levelParam, 10);
        if (
          !isNaN(newLevel) &&
          newLevel >= 0 &&
          newLevel < puzzles.length &&
          newLevel !== gameState.level
        ) {
          // Only show loading if we're actually changing levels
          setIsLoading(true);
          setTimeout(() => {
            loadLevel(newLevel);
            setIsLoading(false);
          }, 100);
        }
      }
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [gameState.level, loadLevel, hasInitialized]);

  // Calculate stars based on performance (matching legacy logic) - moved to event handler
  // const calculateStars = (stats: any) => {
  //   let stars = 1; // Base star for completion
  //   // Add star for low step count
  //   if (stats.stepsTaken <= 10) stars++;
  //   // Add star for fast completion
  //   if (stats.timeSpent <= 30) stars++;
  //   return Math.min(stars, 3);
  // };

  // Direct movement - no throttling like legacy
  const handleMove = useCallback((direction: Direction) => {
    // Block movement if no lives modal is shown
    if (showNoLivesModal) return;
    movePlayer(direction);
  }, [showNoLivesModal, movePlayer]);

  const handlePowerup = (slot: number) => {
    // Powerup activation is handled directly by PowerupDisplay component
    // which uses the shop context to activate powerups
    console.log(`Powerup slot ${slot} activated via PowerupDisplay`);
  };

  // Level complete modal handlers
  const handleLevelCompleteClose = useCallback(() => {
    setLevelCompleteModalOpen(false);
    setLevelCompleteStats(null);
  }, []);

  const handleContinueToNextLevel = () => {
    handleLevelCompleteClose();

    // Check if player has lives before continuing
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }

    // Return unplayed cards to inventory before loading next level
    cardSystem.returnHandToInventory();

    // Load next level using the game engine's loadNextLevel function
    loadNextLevel();
  };

  const handleRetryLevel = () => {
    // Immediately clear all completion state
    setLevelCompleteModalOpen(false);
    setLevelCompleteStats(null);
    setLevelWon(false);
    setHasShownCompleteModal(false);

    // Check if player has lives before retrying
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }

    // Return unplayed cards to inventory before restarting
    cardSystem.returnHandToInventory();

    // Restart the current level, not go to next level
    restartLevel();
  };

  // Obstacle tutorial handlers
  const handleObstacleTutorialClose = useCallback(() => {
    // Mark tutorial as shown BEFORE closing (read from ref to avoid stale closure).
    const obs = currentObstacleRef.current;
    if (obs) markObstacleTutorialShown(obs);
    // Close the modal -- Modal's AnimatePresence handles the exit animation.
    // Keep currentObstacle value so the modal exit animation still has content.
    setObstacleModalOpen(false);
  }, []);

  const handleLevelSelect = (levelIndex: number) => {
    closeModal();

    // Check if player has lives before loading level
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }

    // Update URL to reflect the selected level
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('level', levelIndex.toString());
    window.history.pushState({}, '', newUrl.toString());
    
    loadLevel(levelIndex);
  };

  const handleEscape = () => {
    // Close any open modals first (level complete, shop, level select, game over, settings)
    if (activeModal || levelCompleteModalOpen || obstacleModalOpen || gameOverModalOpen || showSettingsModal) {
      if (activeModal) closeModal();
      if (levelCompleteModalOpen) handleLevelCompleteClose();
      if (obstacleModalOpen) handleObstacleTutorialClose();
      if (gameOverModalOpen) setGameOverModalOpen(false);
      if (showSettingsModal) setShowSettingsModal(false);
    } else {
      // During gameplay with no modals open, escape opens settings menu (matching legacy behavior)
      setShowSettingsModal(true);
    }
  };

  // Create a wrapped restart function that checks for lives
  const handleRestart = useCallback(() => {
    if (shopState.lives <= 0) {
      setShowNoLivesModal(true);
      return;
    }
    cardSystem.returnHandToInventory();
    restartLevel();
    // If restart was triggered from a modal, close it. Never open settings as a side-effect.
    if (activeModal) closeModal();
    if (levelCompleteModalOpen) handleLevelCompleteClose();
    if (obstacleModalOpen) handleObstacleTutorialClose();
    if (gameOverModalOpen) setGameOverModalOpen(false);
    if (showSettingsModal) setShowSettingsModal(false);
  }, [shopState.lives, cardSystem, restartLevel, activeModal, closeModal, levelCompleteModalOpen, handleLevelCompleteClose, obstacleModalOpen, handleObstacleTutorialClose, gameOverModalOpen, showSettingsModal]);

  // Setup keyboard controls
  useKeyboardControls({
    onMove: handleMove,
    onRestart: handleRestart,
    onHome: onGoHome,
    onSettings: () => setShowSettingsModal(true),
    onShop: () => {
      // Support both modal and route navigation
      if (isMobile) {
        openModal('shop' as any); // Mobile: use modal
      } else {
        onOpenShop(); // Desktop: use route
      }
    },
    onStats: onToggleStats,
    onPowerup: handlePowerup,
    onEscape: handleEscape,
    onEnter: () => {},
    onLevelSelect: () => {
      // Support both modal and route navigation
      if (isMobile) {
        openModal('levelSelect' as any); // Mobile: use modal
      } else {
        onLevelSelect(); // Desktop: use route
      }
    },
    disabled: !gameState.isPlaying || showNoLivesModal || isMovementLocked, // Disable all controls when no lives modal is shown or during animations
    isHomeScreen: false,
  });


  // Show loading screen while loading
  if (isLoading) {
    return (
      <div className="game-container">
        <div className="loading-overlay">
          <div className="loading-content flex flex-col justify-center items-center gap-4">
            <div className="pixel-spinner"></div>
            <div className="loading-text">Loading Level...</div>
          </div>
        </div>
      </div>
    );
  }

  const currentWorld = getWorldForLevel(gameState.level);

  return (
    <div
      id="game-container"
      className={`game-container game-container--${currentWorld.id}`}
    >
      {/* Touch zone with game board - exact replica */}
      <div
        id="touchzone"
        className="touchzone flex flex-col gap-4 items-center justify-center relative"
      >
        <ProceduralBackground level={gameState.level} gameState={gameState} />
        <GameBoard
          gameState={gameState}
          hintPath={hintPath}
          hintPathHazards={hintPathHazards}
          onCellClick={warpToPosition}
          onMovementLockChange={setIsMovementLocked}
          onExitAnimationComplete={triggerLevelCompleteModal}
          restartCounter={restartCounter}
        />
        
        {/* Commentary System */}
        <Commentary />

        <div
          id="instructions"
          className={`instructions absolute bottom-[14vh] ${
            gameState.isTutorial ? 'visible' : 'hidden'
          }`}
        >
          {gameState.isTutorial && instructionsText}
        </div>
         {!gameState.isTutorial && <Timer seconds={0} minutes={0} isRunning={gameState.isPlaying} isSlowed={shopState.powerups.slowTime > 0} />}

        {/* Use touch controls on mobile, regular controls on desktop */}
        {isMobile ? (
          <TouchControls
            onMove={handleMove}
            disabled={!gameState.isPlaying || showNoLivesModal || isMovementLocked}
            showVisualFeedback={true}
          />
        ) : (
          <Controls onMove={handleMove} />
        )}
      </div>

      {/* Timer display */}
      <div id="timer" className="timer"></div>

      {/* Popup container - will be populated dynamically */}
      <div id="popup" className="hidden">
        <h1 id="popup-title"></h1>
        <p id="popup-subtitle"></p>
      </div>

      {/* Enhanced mobile elements */}
      <div id="swipe-indicator" className="swipe-indicator"></div>

      {/* Game HUD - MinimalHUD pill + expanded pause overlay */}
      <MinimalHUD
        gameState={gameState}
        isExpanded={hudExpanded}
        onToggle={handleHudToggle}
        onOpenSettings={() => setShowSettingsModal(true)}
        onRestart={() => { setHudExpanded(false); setPaused(false); cardSystem.returnHandToInventory(); restartLevel(); }}
        onQuit={() => { setHudExpanded(false); setPaused(false); cardSystem.returnHandToInventory(); onGoHome(); }}
        onPlayCard={handlePlayCard}
        hand={cardSystem.hand}
      />

      {/* Persistent player stats bar - always visible during gameplay */}
      {!hudExpanded && (
        <div style={{ position: 'fixed', top: 38, left: '50%', transform: 'translateX(-50%)', zIndex: 4999 }}>
          <PlayerStatusBar
            mode="hud"
            worldId={currentWorld.id}
            score={gameState.gameStats.score}
            lives={shopState.lives}
            coins={shopState.coins}
            gems={shopState.gems}
            streak={shopManager?.getDailyRewardStatus()?.streak ?? 0}
          />
        </div>
      )}

      {/* Powerup Display */}
      <PowerupDisplay
        onUsePowerup={powerupId => {
          // Show powerup tutorial the first time this powerup is used
          const powerupTutorialKey = `obstacle_tutorial_powerup_${powerupId}`;
          if (!localStorage.getItem(powerupTutorialKey)) {
            window.dispatchEvent(new CustomEvent('newObstacle', {
              detail: { obstacle: `powerup_${powerupId}` }
            }));
          }
          // Apply powerup effects through the card/game system.
          // activatePowerUp already decremented the inventory count.
          switch (powerupId) {
            case 'shield':
              // Re-add via card effect so the game engine's hasShield() stays true.
              playCardEffect('shield' as any);
              break;
            case 'invincibility':
              // Invincibility is passive while count > 0. The activation already
              // decremented, so add one back to keep the flag active.
              shopManager.addPowerup('invincibility');
              break;
            case 'doubleCoins':
              // Passive while count > 0. Add back so the coin multiplier stays active.
              shopManager.addPowerup('doubleCoins');
              break;
            case 'slowTime':
              // Passive while count > 0. Add back so the timer check stays active.
              shopManager.addPowerup('slowTime');
              break;
            case 'hint':
              // Show the optimal path using the scout card effect.
              playCardEffect('scout' as any);
              break;
            case 'undo':
              playCardEffect('rewind' as any);
              break;
            case 'skipLevel':
              loadNextLevel();
              break;
            case 'teleport':
              playCardEffect('warp' as any);
              break;
          }
        }}
      />

      {/* Card Hand (floating at bottom during gameplay) */}
      {gameState.isPlaying && !showCardPicker && cardSystem.hand.length > 0 && (
        <CardHand
          cards={cardSystem.hand}
          onPlayCard={handlePlayCard}
        />
      )}

      {/* Card Play Effect Overlay */}
      <CardPlayEffect playedCard={playedCard} />

      {/* Card Picker (pre-level selection overlay) */}
      {showCardPicker && (
        <CardPicker
          inventory={cardSystem.inventory}
          onConfirm={handleCardPickerConfirm}
          onSkip={handleCardPickerSkip}
        />
      )}

      {/* Modal System Integration */}
      <LevelSelectModal
        isOpen={isModalOpen('levelSelect')}
        onClose={closeModal}
        onLevelSelect={handleLevelSelect}
        currentLevel={gameState.level}
      />

      <ShopModal isOpen={isModalOpen('shop')} onClose={closeModal} />

      {/* Level Complete Modal */}
      {levelCompleteStats && (
        <LevelCompleteModal
          isOpen={levelCompleteModalOpen}
          levelWon={levelWon}
          title={levelWon ? 'Level Complete!' : 'Level Failed'}
          subtitle={
            levelWon ? 'Great job! Ready for the next challenge?' : "Don't give up! Try again?"
          }
          stats={levelCompleteStats}
          onContinue={handleContinueToNextLevel}
          onRetry={handleRetryLevel}
          onClose={handleLevelCompleteClose}
        />
      )}

      {/* Obstacle Tutorial Modal */}
      <ObstacleTutorialModal
        isOpen={obstacleModalOpen}
        obstacleType={currentObstacle}
        onClose={handleObstacleTutorialClose}
      />

      {/* Game Over Modal */}
      {gameOverStats && (
        <GameOverModal
          isOpen={gameOverModalOpen}
          onClose={() => setGameOverModalOpen(false)}
          onRestart={() => {
            setGameOverModalOpen(false);
            handleRestart();
          }}
          onHome={() => {
            setGameOverModalOpen(false);
            cardSystem.returnHandToInventory();
            onGoHome();
          }}
          onShop={() => {
            setGameOverModalOpen(false);
            openModal('shop');
          }}
          stats={gameOverStats}
        />
      )}

      {/* No Lives Modal */}
      <NoLivesModal
        isOpen={showNoLivesModal}
        onClose={() => {
          // Only allow closing if player now has lives
          if (shopState.lives > 0) {
            setShowNoLivesModal(false);
          } else {
            // If still no lives, go back to home screen
            onGoHome();
          }
        }}
        onShop={() => {
          setShowNoLivesModal(false);
          if (isMobile) {
            openModal('shop' as any);
          } else {
            onOpenShop();
          }
        }}
      />
      
      {/* Settings Modal */}
      <SettingsMenuWrapper
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onRestart={handleRestart}
        onGoHome={onGoHome}
        onToggleFullscreen={() => {
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
          } else {
            document.exitFullscreen();
          }
        }}
      />

      {/* Daily Reward Modal */}
      <DailyRewardModal
        isOpen={showDailyReward}
        onClose={() => setShowDailyReward(false)}
      />
    </div>
  );
}

export default GameScreen;
